import { Tooltip } from 'antd';
import Link from 'next/link';
import styled from 'styled-components';

import CustomizableLessonScreenshotOne from '../../../assets/images/LandingPage/CustomizableLessonScreenshotOne.png';
import CustomizableLessonScreenshotThree from '../../../assets/images/LandingPage/CustomizableLessonScreenshotThree.png';
import CustomizableLessonScreenshotTwo from '../../../assets/images/LandingPage/CustomizableLessonScreenshotTwo.png';
import ReverseTrialScreenshot from '../../../assets/images/LandingPage/ReverseTrialScreenshot.png';
import theme from '../../../assets/theme';
import { NextPageWithLayout } from '../../layouts';

const Container = styled.div`
  width: 100%;
`;

const BeigeSection = styled.section`
  background-color: ${theme.colors.background};
  padding: 40px 20px;
  text-align: left;

  h2 {
    font-size: 30px;
    max-width: 900px;
    margin: 0 auto;
  }

  p {
    font-size: 20px;
    font-weight: normal;
  }

  @media (max-width: 700px) {
    padding: 40px 10px;
  }
`;

const GreenSection = styled.section`
  background-color: ${theme.colors.green};
  padding: 40px 20px;
  text-align: center;
  color: white;

  h2 {
    font-size: 30px;
    color: white;
    max-width: 900px;
    margin: 0 auto;
  }

  h3 {
    font-size: 24px;
    color: white;
    max-width: 900px;
    margin: 0 auto;
  }

  p {
    font-size: 20px;
    font-weight: normal;
  }

  @media (max-width: 700px) {
    padding: 40px 10px;
  }
`;

const ColumnsWrapper = styled.div<{ columnNumber?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columnNumber || 2}, 1fr);
  gap: 20px;
  margin: 0 auto;
  max-width: 900px;
  align-items: center;

  h2 {
    text-align: left;
  }

  p {
    text-align: left;
    font-weight: normal;
    font-size: 18px;
  }

  @media (max-width: 880px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const ColumnsWrapperInverted = styled.div<{ columnNumber?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columnNumber || 2}, 1fr);
  gap: 20px;
  margin: 0 auto;
  max-width: 900px;
  align-items: center;
  direction: rtl;

  > * {
    direction: ltr;
  }

  h2 {
    text-align: left;
  }

  p {
    text-align: left;
    font-weight: normal;
    font-size: 18px;
  }

  @media (max-width: 880px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Title = styled.strong`
  font-weight: 800;
  text-align: left;
`;

const Emoji = styled.span`
  margin-right: 10px;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  justify-content: center;
  width: fit-content;
  margin: 0 auto;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    width: auto;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  transform: scale(1);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  background-color: ${theme.colors.white};

  &:hover {
    transform: scale(1.05);
  }

  user-select: none;
`;

const EmojiTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledEmoji = styled(Emoji)`
  font-size: 2em;
`;

const ImageWrapper = styled.div`
  img {
    max-width: 100%;
    border-radius: 10px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }
`;

const SlideshowContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 200px;
  margin: auto;
`;

const Image = styled.img`
  position: absolute;
  width: 80%;
  height: auto;
  border-radius: 10px;
  border: 4px solid ${theme.colors.black};
  transition: transform 0.3s ease;
  box-shadow: rgba(0, 0, 0, 0.25) 0px 54px 55px, rgba(0, 0, 0, 0.12) 0px -12px 30px,
    rgba(0, 0, 0, 0.12) 0px 4px 6px, rgba(0, 0, 0, 0.17) 0px 12px 13px,
    rgba(0, 0, 0, 0.09) 0px -3px 5px;

  &:nth-child(1) {
    z-index: 3;
    transform: translate(30%, 30%);
  }

  &:nth-child(2) {
    z-index: 2;
    transform: translate(15%, 15%);
  }

  &:nth-child(3) {
    z-index: 1;
    transform: translate(0%, 0%);
  }
`;

const FeaturesSection = () => {
  return (
    <BeigeSection>
      <ColumnsWrapper>
        <div>
          <h2>What is part of the trial?</h2>
          <p>
            Shy of school plan features (like SSO or shared library), you get everything
            that comes with Curipod&apos;s{' '}
            <Link href="/pricing">
              <a style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                School Plan
              </a>
            </Link>{' '}
            license. If it&apos;s available in Curipod, it&apos;s available to you - for a
            limited time!
          </p>
        </div>
        <CardsContainer>
          <Tooltip
            title={'Access to all test prep and immersive classroom experiences.'}
            placement="top"
          >
            <Card>
              <EmojiTitleContainer>
                <StyledEmoji role="img" aria-label="crown">
                  👑
                </StyledEmoji>
                <Title>Test Prep. Material</Title>
              </EmojiTitleContainer>
            </Card>
          </Tooltip>
          <Tooltip
            title={
              'Customize your AI Feedback with your own rubrics or multiple languages.'
            }
            placement="top"
          >
            <Card>
              <EmojiTitleContainer>
                <StyledEmoji role="img" aria-label="robot">
                  🤖
                </StyledEmoji>
                <Title>Standards alignment (national and state)</Title>
              </EmojiTitleContainer>
            </Card>
          </Tooltip>
          <Tooltip
            title={
              "Engage your students with Curipod's entire catalog of customizable lessons."
            }
            placement="bottom"
          >
            <Card>
              <EmojiTitleContainer>
                <StyledEmoji role="img" aria-label="movie">
                  🎬
                </StyledEmoji>
                <Title>Unlimited Content Access</Title>
              </EmojiTitleContainer>
            </Card>
          </Tooltip>
          <Tooltip
            title={
              'Unlimited translations and feedback in multiple languages for the best multilingual classroom experience.'
            }
            placement="bottom"
          >
            <Card>
              <EmojiTitleContainer>
                <StyledEmoji role="img" aria-label="globe">
                  🌎
                </StyledEmoji>
                <Title>Billingual Support</Title>
              </EmojiTitleContainer>
            </Card>
          </Tooltip>
        </CardsContainer>
      </ColumnsWrapper>
    </BeigeSection>
  );
};

const DeadlineSection = () => {
  return (
    <GreenSection>
      <ColumnsWrapperInverted>
        <div>
          <h2>When does the trial expire?</h2>
          <p>
            You can easily check how long you have left on your trial by logging in and
            visiting your{' '}
            <Link href="/home">
              <a
                style={{
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  color: theme.colors.white,
                }}
              >
                Home
              </a>
            </Link>{' '}
            page. You&apos;ll see your trial&apos;s deadline right under Curipod&apos;s
            logo.
          </p>
        </div>
        <ImageWrapper>
          <img src={ReverseTrialScreenshot.src} alt="trial duration screenshot" />
        </ImageWrapper>
      </ColumnsWrapperInverted>
    </GreenSection>
  );
};

const AfterDeadlineSection = () => {
  return (
    <BeigeSection>
      <ColumnsWrapper>
        <div>
          <h2>What happens after?</h2>
          <p>
            After the trial is over, you&apos;ll be downgraded to Curipod&apos;s{' '}
            <Link href="/pricing">
              <a style={{ fontWeight: 'bold', textDecoration: 'underline' }}>Free Plan</a>
            </Link>
            . However, you&apos;ll retain access to any lessons you create during your
            trial. It doesn&apos;t matter whether or not you&apos;ve used features from
            the{' '}
            <Link href="/pricing">
              <a style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                School Plan
              </a>
            </Link>{' '}
            .
          </p>
        </div>
        <SlideshowContainer>
          <Image
            src={CustomizableLessonScreenshotOne.src}
            alt="Customizable Lesson Screenshot One"
          />
          <Image
            src={CustomizableLessonScreenshotTwo.src}
            alt="Customizable Lesson Screenshot Two"
          />
          <Image
            src={CustomizableLessonScreenshotThree.src}
            alt="Customizable Lesson Screenshot Three"
          />
        </SlideshowContainer>
      </ColumnsWrapper>
    </BeigeSection>
  );
};

const TrialPageContent: NextPageWithLayout = () => {
  return (
    <Container>
      <FeaturesSection />
      <DeadlineSection />
      <AfterDeadlineSection />
    </Container>
  );
};

export default TrialPageContent;
